import { useMemo } from "react";
import { Cake, Confetti } from "@phosphor-icons/react";
import { formatDateIT } from "@/lib/formatDate";
import { OverviewCard, OverviewEmpty } from "./Card";

/**
 * Extra widget (f) — Anniversario percorso.
 *
 * "Cliente da N..." calcolato dalla data di prima fase o dal `created_at`
 * del cliente. Mostra anche età/compleanno se `date_of_birth` è presente.
 */

function diffMonths(fromIso) {
  if (!fromIso) return null;
  const m = fromIso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const from = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  let months = (now.getFullYear() - from.getFullYear()) * 12 + (now.getMonth() - from.getMonth());
  if (now.getDate() < from.getDate()) months -= 1;
  return Math.max(0, months);
}

function ageFromDob(dob) {
  if (!dob) return null;
  const m = dob.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const birth = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  if (now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age;
}

function daysUntilNextBirthday(dob) {
  if (!dob) return null;
  const m = dob.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const nextBday = new Date(now.getFullYear(), Number(m[2]) - 1, Number(m[3]));
  if (nextBday < now) nextBday.setFullYear(now.getFullYear() + 1);
  return Math.round((nextBday - now) / 86400000);
}

export function ClientAnniversaryWidget({ client, phases }) {
  const data = useMemo(() => {
    const startCandidates = [client?.created_at, ...(phases || []).map((p) => p.start_date)]
      .filter(Boolean)
      .sort();
    const journeyStart = startCandidates[0] || null;
    const months = diffMonths(journeyStart);
    const age = ageFromDob(client?.date_of_birth);
    const daysToBday = daysUntilNextBirthday(client?.date_of_birth);
    return { journeyStart, months, age, daysToBday };
  }, [client, phases]);

  const hasAnything = data.journeyStart || data.age != null;

  return (
    <OverviewCard
      title="Anniversario percorso"
      icon={Cake}
      iconColor="text-[#FF2D55]"
      testId="overview-anniversary"
    >
      {!hasAnything ? (
        <OverviewEmpty message="Nessuna data disponibile per calcolare l'anniversario." />
      ) : (
        <div className="space-y-2.5">
          {data.journeyStart && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-zinc-500">Cliente da</span>
              <span className="text-white font-semibold">
                {data.months >= 12
                  ? `${Math.floor(data.months / 12)} ann${Math.floor(data.months / 12) === 1 ? "o" : "i"}${data.months % 12 ? ` e ${data.months % 12} mesi` : ""}`
                  : `${data.months} mes${data.months === 1 ? "e" : "i"}`}
                <span className="text-zinc-600 ml-1.5">· dal {formatDateIT(data.journeyStart)}</span>
              </span>
            </div>
          )}
          {data.age != null && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-zinc-500">Età</span>
              <span className="text-white font-semibold">{data.age} anni</span>
            </div>
          )}
          {data.daysToBday != null && data.daysToBday <= 30 && (
            <div className="flex items-center justify-between text-xs p-2 rounded-md bg-[#FF2D55]/10 border border-[#FF2D55]/20">
              <span className="text-[#FF2D55] font-medium flex items-center gap-1.5">
                <Confetti size={14} weight="fill" /> Compleanno
              </span>
              <span className="text-[#FF2D55] font-bold">
                {data.daysToBday === 0 ? "oggi!" : `tra ${data.daysToBday} g`}
              </span>
            </div>
          )}
        </div>
      )}
    </OverviewCard>
  );
}
